// Post card (feed, profile, post page) + the "share to feed" dialog.

import { h, toast } from './dom';
import { api, toRef, type Emoji, type Photo, type Post, type Session, type User, type Visibility } from '../api/client';
import { avatar, avatarStack, confirmBox, guard, photoImg, sheet, timeAgo } from './components';
import { openViewer } from './viewer';
import { REACTION_EMOJI, sortEmojiCounts } from './reaction-text';
import { openReactors } from './reactors';

export const EMOJI: Record<Emoji, string> = REACTION_EMOJI;
export const VIS: Record<Visibility, { icon: string; name: string }> = {
  FRIENDS: { icon: '🫂', name: 'Bạn bè' },
  CLOSE_FRIENDS: { icon: '💖', name: 'Bạn thân' },
  PARTICIPANTS: { icon: '🔒', name: 'Chỉ người trong ảnh' },
};

export function openPhoto(p: Photo, title: string) {
  void openViewer({ title, previewUrl: null, photo: toRef(p), filename: `booth-${p.kind.toLowerCase()}${p.index + 1}.jpg` });
}

const others = (post: Post) => post.people.filter((u) => u.id !== post.author.id);

function peopleLine(post: Post) {
  const o = others(post);
  if (!o.length) return null;
  return h('span.with', {}, ' cùng ',
    ...o.flatMap((u, i) => [i ? ', ' : '', h('a', { href: `#/u/${u.id}` }, u.displayName)]));
}

export function postCard(post: Post, me: User,
  opts: { onDeleted?: () => void; detail?: boolean; showCollab?: boolean; eager?: boolean } = {}) {
  const card = h('article.post', { 'data-post-id': post.id });
  const paint = (p: Post) => {
    // reaction responses come from /api/posts/{id} without feed thumbs: keep the ones we have
    const thumbs = new Map(post.photos.map((x) => [x.id, x.thumbUrl]));
    p = { ...p, photos: p.photos.map((x) => (x.thumbUrl ? x : { ...x, thumbUrl: thumbs.get(x.id) ?? null })) };
    post = p;
    const main = p.photos.find((x) => x.kind === 'STRIP') ?? p.photos[0];
    const rest = p.photos.filter((x) => x !== main);
    const reactBar = h('div.reacts', {}, ...(Object.keys(EMOJI) as Emoji[]).map((e) =>
      h('button.react', {
        class: p.myReaction === e ? 'on' : '',
        title: e,
        'aria-pressed': String(p.myReaction === e),
        'aria-label': `${e}${p.reactions[e] ? ` (${p.reactions[e]})` : ''}`,
        onclick: async () => {
          const np = await guard(p.myReaction === e ? api.unreact(p.id) : api.react(p.id, e));
          if (np) paint(np);
        },
      }, EMOJI[e], p.reactions[e] ? h('small', {}, String(p.reactions[e])) : null)));
    const menu = p.author.id === me.id
      ? h('button.icon.small', {
        title: 'Xoá bài',
        'aria-label': 'Xoá bài',
        onclick: async () => {
          if (!(await confirmBox('Xoá bài đăng này? Ảnh vẫn còn trong thư viện.', 'Xoá'))) return;
          try {
            await api.deletePost(p.id);
            toast('Đã xoá bài');
            opts.onDeleted?.();
          } catch (e) {
            toast((e as Error).message, 3500);
          }
        },
      }, '🗑')
      : null;
    card.replaceChildren(...[
      h('header.phead', {},
        h('a.phead-av', { href: `#/u/${p.author.id}`, 'aria-label': p.author.displayName },
          others(p).length ? avatarStack([p.author, ...others(p)], 34, 3) : avatar(p.author, 42)),
        h('div.pmeta', {},
          h('div', {}, h('a.pname', { href: `#/u/${p.author.id}` }, p.author.displayName), peopleLine(p)),
          h('small', {}, opts.showCollab && p.collab ? h('span.collab', {}, '🎞 Chụp chung') : null,
            timeAgo(p.createdAt), ' · ', VIS[p.visibility].icon, ' ', VIS[p.visibility].name, expiryLabel(p))),
        menu),
      p.caption ? h('p.caption', {}, p.caption) : null,
      main ? h('div.pphotos', {},
        h('button.pmain', {
          'aria-label': main.kind === 'STRIP' ? 'Xem dải ảnh' : 'Xem ảnh',
          style: main.width && main.height ? { aspectRatio: `${main.width} / ${main.height}` } : {},
          onclick: () => openPhoto(main, main.kind === 'STRIP' ? 'Dải ảnh' : 'Ảnh'),
        }, photoImg(main, 'pimg', { eager: opts.eager })),
        rest.length ? h('div.pthumbs', {}, ...rest.map((x) =>
          h('button.pthumb', { 'aria-label': `Kiểu ${x.index + 1}`, onclick: () => openPhoto(x, `Kiểu ${x.index + 1}`) }, photoImg(x, 'pimg')))) : null) : null,
      h('footer.pfoot', {},
        reactBar,
        reactionSummary(p),
        opts.detail ? null : h('a.commentlink', { href: `#/post/${p.id}`, 'data-comment-count': String(p.commentCount) },
          p.commentCount ? `💬 ${p.commentCount} bình luận` : '💬 Bình luận')),
    ].filter((x): x is HTMLElement => !!x));
  };
  paint(post);
  return card;
}

/** "❤️😆 7": who reacted (opens the Cảm xúc sheet). */
function reactionSummary(p: Post) {
  const n = p.reactionCount || Object.values(p.reactions).reduce((a, b) => a + (b ?? 0), 0);
  if (!n) return null;
  const top = sortEmojiCounts(p.reactions).slice(0, 3);
  return h('button.rsum', { 'aria-label': `Xem ${n} cảm xúc`, onclick: () => openReactors(p.id, p.reactions) },
    h('span.rsum-e', { 'aria-hidden': 'true' }, ...top.map(([e]) => h('i', {}, REACTION_EMOJI[e]))), h('b', {}, String(n)));
}

/** "⏳ còn 5 giờ" while friends can see the post; "🔒 chỉ người trong ảnh" after its 24 h. */
export function expiryLabel(p: Post) {
  if (!p.expiresAt || p.visibility === 'PARTICIPANTS') return null;
  const left = new Date(p.expiresAt).getTime() - Date.now();
  if (left <= 0) return h('span.expiry.over', { title: 'Bạn bè không còn thấy bài này' }, ' · 🔒 chỉ người trong ảnh');
  const hours = Math.floor(left / 3_600_000);
  const text = hours >= 1 ? `còn ${hours} giờ` : `còn ${Math.max(1, Math.ceil(left / 60_000))} phút`;
  return h('span.expiry', { title: 'Bạn bè thấy bài này trong 24 giờ' }, ` · ⏳ ${text}`);
}

/** Share a finished photo session to the feed. */
export function shareDialog(s: Session, onDone?: (p: Post) => void) {
  const chosen = new Set(s.photos.filter((p) => p.kind === 'STRIP').map((p) => p.id));
  let vis: Visibility = 'FRIENDS';
  const caption = h('textarea.field', { rows: 2, maxLength: 2000, placeholder: 'Viết gì đó… (không bắt buộc)' });
  const grid = h('div.sharegrid', {}, ...s.photos.map((p) => {
    const b = h('button.shareitem', { class: chosen.has(p.id) ? 'on' : '' }, photoImg(p, 'pimg'),
      h('span.gtag', {}, p.kind === 'STRIP' ? 'Dải ảnh' : `#${p.index + 1}`));
    b.onclick = () => { if (chosen.has(p.id)) chosen.delete(p.id); else chosen.add(p.id); b.classList.toggle('on'); };
    return b;
  }));
  const visRow = h('div.chips', {}, ...(Object.keys(VIS) as Visibility[]).map((v) => {
    const b = h('button.chip', { class: v === vis ? 'on' : '' }, VIS[v].icon, ' ', VIS[v].name);
    b.onclick = () => { vis = v; visRow.querySelectorAll('.chip').forEach((c) => c.classList.remove('on')); b.classList.add('on'); };
    return b;
  }));
  const btn = h('button.primary', {}, 'Đăng');
  // Settings → Quyền riêng tư decides how long friends see it
  const lifetime = h('p.tiny.left', {}, '');
  void api.mySettings().then((st) => {
    lifetime.textContent = st.autoDeletePosts ? '⏳ Bạn bè thấy trong 24 giờ, sau đó bài đăng tự xoá hẳn.'
      : st.postsExpire ? '⏳ Bạn bè thấy trong 24 giờ, sau đó chỉ người trong ảnh còn xem được.'
        : 'Bạn bè thấy bài đăng không giới hạn thời gian.';
  }).catch(() => {});
  const close = sheet('Đăng lên bảng tin', h('div.share', {}, h('p.tiny', {}, 'Chọn ảnh'), grid, caption,
    h('p.tiny', {}, 'Ai được xem?'), visRow, lifetime, btn));
  btn.onclick = async () => {
    if (!chosen.size) return toast('Chọn ít nhất 1 ảnh');
    btn.disabled = true;
    const p = await guard(api.createPost(s.id, [...chosen], caption.value, vis), 'Đã đăng ♡');
    btn.disabled = false;
    if (p) { close(); onDone?.(p); }
  };
}
